import { useState, useEffect, useRef, useMemo } from 'react';
import { getApiUrl, getFastApiUrl } from '../../utils/apiConfig';
import { buildQuizSessions, buildExtraQuizSession, TOTAL_QUIZZES } from '../../config/quizConfig';
import QuizCard from './QuizCard';

const QuizInterface = ({ onClose, currentQuizIndex, extraQuizNumber, onFinalQuizComplete, textHash, quizHistory, selectedSubject, selectedTopic, currentPage, onQuizResultSaved }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quizHash, setQuizHash] = useState(null);

  // Ensures question generation runs once per quiz invocation.
  const loadedForIndexRef = useRef(null);

  const [quizSessions, setQuizSessions] = useState(() => buildQuizSessions());
  // An extra quiz (on-demand, timed mode) or the scheduled quiz at currentQuizIndex.
  const session = useMemo(
    () => (extraQuizNumber ? buildExtraQuizSession(extraQuizNumber) : quizSessions[currentQuizIndex]),
    [extraQuizNumber, quizSessions, currentQuizIndex]
  );
  const loadKey = extraQuizNumber ? `extra-${extraQuizNumber}` : currentQuizIndex;

  // Load questions based on quiz type
  useEffect(() => {
    // Generate once per quiz index; skip if already loaded for this one.
    if (loadedForIndexRef.current === loadKey) {
      return;
    }
    loadedForIndexRef.current = loadKey;

    const numQuestions = session.questions;
    const history = quizHistory || { hashes: new Set(), questions: new Set() };

    // Pick `count` questions, preferring ones not yet asked this session.
    const pickQuestions = (pool, count) => {
      const fresh = pool.filter(q => !history.questions.has(q.question));
      const source = fresh.length >= count ? fresh : pool;
      const picked = [...source].sort(() => Math.random() - 0.5).slice(0, count);
      picked.forEach(q => history.questions.add(q.question));
      return picked;
    };

    const finish = (picked) => {
      setQuestions(picked);
      setLoading(false);
    };

    // Random questions from everything cached this session (fallback for regular quizzes, source for the final).
    const fetchFinalQuizQuestions = (count) => {
      fetch(`${getFastApiUrl('api/final-quiz-questions')}?count=${Math.max(count, 4)}`)
        .then(res => res.json())
        .then(data => {
          const pool = data.success && data.questions ? data.questions : [];
          if (pool.length === 0) console.error('QuizInterface: No questions found in final quiz endpoint');
          finish(pickQuestions(pool, count));
        })
        .catch(err => {
          console.error('QuizInterface: Failed to fetch final quiz questions:', err);
          finish([]);
        });
    };

    // OCR path: use the newest screenshot content captured since the last quiz.
    const fetchOcrQuizQuestions = async (count) => {
      let hashes = [];
      try {
        const res = await fetch(`${getFastApiUrl('api/latest-quiz-hashes')}?count=${TOTAL_QUIZZES}`);
        const data = await res.json();
        if (data.success && data.hashes) hashes = data.hashes; // newest first, only hashes with questions
      } catch (err) {
        console.error('QuizInterface: Failed to fetch latest hashes:', err);
      }

      // Newest unquizzed content first; if nothing new has questions yet, stay on the newest content.
      const candidates = [...new Set([hashes.find(h => !history.hashes.has(h)), hashes[0], textHash].filter(Boolean))];

      for (const hash of candidates) {
        try {
          const res = await fetch(`${getFastApiUrl('api/quiz-questions')}/${hash}`);
          if (!res.ok) continue;
          const data = await res.json();
          const pool = data.questions?.questions || [];
          if (pool.length > 0) {
            console.log(`QuizInterface: ${session.name} using hash ${hash} (${history.hashes.has(hash) ? 'reused' : 'new content'})`);
            history.hashes.add(hash);
            setQuizHash(hash);
            finish(pickQuestions(pool, count));
            return;
          }
        } catch (err) {
          console.error(`QuizInterface: Failed to fetch questions for hash ${hash}:`, err);
        }
      }

      console.log(`QuizInterface: No content hash with questions for ${session.name}, using final quiz endpoint as fallback`);
      fetchFinalQuizQuestions(count);
    };

    // Helper function to fetch from Bedrock KB quiz generation endpoint
    const fetchBedrockQuizQuestions = (count) => {
      const materialId = selectedTopic?.value;
      if (!materialId || typeof materialId !== 'number') {
        console.log('QuizInterface: No numeric material_id, falling back to OCR pipeline');
        fetchFinalQuizQuestions(count);
        return;
      }

      console.log(`QuizInterface: Generating quiz for material_id=${materialId}, page=${currentPage}`);
      fetch(getApiUrl('quiz-questions/generate'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ material_id: materialId, num_questions: count, page: currentPage || 1 }),
      })
        .then(res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then(data => {
          if (data.success && data.questions && data.questions.length > 0) {
            finish(pickQuestions(data.questions, count));
          } else {
            console.warn('QuizInterface: Bedrock KB returned no questions, falling back');
            fetchFinalQuizQuestions(count);
          }
        })
        .catch(err => {
          console.error('QuizInterface: Bedrock KB quiz generation failed, falling back:', err);
          fetchFinalQuizQuestions(count);
        });
    };

    const quizSource = process.env.REACT_APP_QUIZ_SOURCE || 'ocr';

    setLoading(true);
    setQuestions([]);

    if (quizSource === 'bedrock') {
      fetchBedrockQuizQuestions(numQuestions);
    } else if (session.isFinal) {
      fetchFinalQuizQuestions(numQuestions);
    } else {
      fetchOcrQuizQuestions(numQuestions);
    }
  }, [textHash, currentQuizIndex, loadKey, quizHistory, session, selectedTopic, currentPage]);

  const handleQuizComplete = (quizId) => {
    setQuizSessions(prev =>
      prev.map(quiz =>
        quiz.id === quizId
          ? { ...quiz, completed: true }
          : quiz
      )
    );
    onClose();
  };

  const handleStudySessionComplete = () => {
    setQuizSessions(prev =>
      prev.map(quiz => ({ ...quiz, completed: false }))
    );
    onClose();
  };


  const currentQuizSession = {
    ...session,
    hash: quizHash || textHash,
    subjectName: selectedSubject?.label || 'General',
    topicName: selectedTopic?.label || 'Reading Session'
  };

  return (
    <div className="quiz-interface-container">
      <button className="quiz-close-button" onClick={onClose}>×</button>
      {loading ? (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <p>Loading quiz questions...</p>
        </div>
      ) : (
        <QuizCard
          quizSession={currentQuizSession}
          questions={questions}
          onQuizComplete={() => handleQuizComplete(currentQuizSession.id)}
          onStudySessionComplete={handleStudySessionComplete}
          onClose={onClose}
          isLastQuiz={session.isFinal}
          onFinalQuizComplete={onFinalQuizComplete}
          onQuizResultSaved={onQuizResultSaved}
        />
      )}
    </div>
  );
};

export default QuizInterface;
